'use client';

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { OrExteriorSwitcher, OrCarouselHero } from '@/components/organisms';
import { AtHeader, AtText, AtClickable, AtIcon } from '@/components/atoms';
import { MlInfoTooltip } from '@/components/molecules/ml-info-tooltip/ml-info-tooltip';
import { EIcons } from '@/components/atoms/at-icon/icons';
import { EClickableVariants } from '@/components/atoms/at-clickable/at-clickable.types';
import { ETextVariants } from '@/components/atoms/at-text/at-text.types';
import {
  ESetLocationType,
  useSetLocationModal,
} from '@/context/setLocationContext';

import type { Swiper as SwiperType } from 'swiper/types';
import { useLocation } from '@/context/locationContext';
import { useHomePlanData } from './hooks/useHomePlanData';
import { LoginModalContext } from '@/context/loginModalContext';
import { parseImageFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';
import { TOrHomePlanHeroCSProps } from '@/lib/contentstack-sdk/types';
import Skeleton from 'react-loading-skeleton';
import { useGridScrollRestore } from '@/context/gridScrollRestoreContext';

interface Props {
  homePlanName: string;
  heroTooltipBody?: string;
  autoplay?: boolean;
  planId: number;
  heroSection: TOrHomePlanHeroCSProps[];
  hasThreeD: boolean;
}

export const TmHomePlanHeroClient = ({
  homePlanName,
  heroTooltipBody,
  planId,
  autoplay,
  heroSection,
  hasThreeD,
}: Props) => {
  const {
    setElevationIdForLogin,
    setPlanIdForLogin,
    setStudioIdForLogin,
    buildLoginUrl,
  } = useContext(LoginModalContext);

  const { setLastVisitedUid, setTimestamp } = useGridScrollRestore();

  useEffect(() => {
    setLastVisitedUid('home_plan_detail');
    setTimestamp(Date.now());
  }, [setLastVisitedUid, setTimestamp]);

  const [has3D, setHas3D] = useState(false);
  const [is3DLoading, setIs3DLoading] = useState(true);
  const [loginUrl, setLoginUrl] = useState<string>('');
  const swiperRef = useRef<SwiperType | null>(null);
  const { handleOpen } = useSetLocationModal();
  const { currentBuildStudio } = useLocation();
  const {
    planDetailData,
    price,
    selectedExterior,
    exteriorCount,
    availableExteriors,
    handleSelectExterior,
    baseElevation,
    locationConflict,
    userSelectedExterior,
  } = useHomePlanData(planId);

  useEffect(() => {
    if (is3DLoading) return;

    const url = buildLoginUrl({
      StudioId: currentBuildStudio
        ? currentBuildStudio.Id?.toString()
        : undefined,
      ElevationId: String(selectedExterior?.id) || String(baseElevation?.Id),
      PlanId: `${planId}`,
      LoginAction: has3D ? 'CUSTOMIZE_PLAN_3D' : 'CUSTOMIZE_PLAN',
    });

    setLoginUrl(url);
  }, [
    is3DLoading,
    buildLoginUrl,
    currentBuildStudio,
    selectedExterior?.id,
    baseElevation?.Id,
    planId,
    has3D,
  ]);

  useEffect(() => {
    const fetch3dData = async () => {
      setIs3DLoading(true);
      setHas3D(!!currentBuildStudio?.supports3D && hasThreeD);
      setIs3DLoading(false);
    };

    fetch3dData();
  }, [currentBuildStudio?.Id, currentBuildStudio?.supports3D, hasThreeD]);

  // This useEffect updates the ids for the LoginModal context in case we have a dynamic loginModal url
  // when any of the values in the dependency array is updated, and we set them to -1 to know we don't have them.
  // i.e.: :openLoginModal?StudioId={studioId}&ElevationId={elevationId}&PlanId={planId}
  useEffect(() => {
    if (currentBuildStudio?.Id) setStudioIdForLogin(currentBuildStudio.Id);
    if (planId) setPlanIdForLogin(planId);
    if (selectedExterior?.id) setElevationIdForLogin(selectedExterior.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, currentBuildStudio?.Id, selectedExterior?.id]);

  const customizeAndPriceUrl = buildLoginUrl({
    StudioId: currentBuildStudio
      ? currentBuildStudio.Id?.toString()
      : undefined,
    ElevationId: (selectedExterior?.id ?? baseElevation?.Id)?.toString(),
    PlanId: `${planId}` || undefined,
    LoginAction: has3D ? 'CUSTOMIZE_PLAN_3D' : 'CUSTOMIZE_PLAN',
  });

  const heroSlides = useMemo(() => {
    const slides = [];

    if (selectedExterior && !locationConflict) {
      slides.push({
        id: 'exterior-slide',
        image: selectedExterior.image,
        disclaimer: 'May be shown with optional features',
      });
    }

    heroSection.forEach((item, index) => {
      if (item.cta_slide?.slide_content) {
        const content = item.cta_slide.slide_content;
        const image = content.find((c) => c.slide_image)?.slide_image;
        const cta = content.find((c) => c.cta)?.cta;
        const header = content.find((c) => c.header)?.header?.header;

        if (image) {
          slides.push({
            id: `cta-slide-${index}`,
            image: parseImageFromCMS(image),
            header,
            button: {
              text: cta?.url?.title,
              url: cta?.url?.href,
            },
          });
        }
      } else if (item.slide_image) {
        slides.push({
          id: `slide-${index}`,
          image: parseImageFromCMS(item.slide_image),
        });
      }
    });

    return slides;
  }, [heroSection, selectedExterior, locationConflict]);

  const handleCustomizeAndPrice = () => {
    handleOpen({
      type: ESetLocationType.PRICING,
      redirect: customizeAndPriceUrl,
    });
  };

  const prevExteriorRef = useRef<any>(null);

  useEffect(() => {
    const changed = prevExteriorRef.current?.id !== selectedExterior?.id;
    if (changed && userSelectedExterior.current && swiperRef.current) {
      swiperRef.current?.slideToLoop(0);
      swiperRef.current?.autoplay?.stop();
      userSelectedExterior.current = false;
    }
    prevExteriorRef.current = selectedExterior;
  }, [selectedExterior, userSelectedExterior]);

  const planDetailExists = planDetailData?.length > 0;
  return (
    <>
      {planDetailExists && !locationConflict ? (
        <OrExteriorSwitcher
          copy={{
            drawerTitle: 'Exterior Options',
            drawerSubtitle: `For the ${homePlanName} Floorplan`,
            contentDescription: 'Exterior Styles',
          }}
          exteriorCount={exteriorCount}
          exteriorStyles={availableExteriors}
          selectedExterior={selectedExterior}
          onExteriorSelect={handleSelectExterior}
        />
      ) : (
        <Skeleton
          baseColor="var(--color-neutral-100)"
          highlightColor="var(--color-neutral-200)"
          height={200}
          className="rounded-30"
        />
      )}

      <OrCarouselHero
        autoplay={autoplay}
        slideData={heroSlides}
        swiperRef={swiperRef}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        textContent={
          <div className="mb-9.5 lg:mb-0">
            <AtHeader element="h1" alt className="mb-5 lg:mb-12">
              {homePlanName}
            </AtHeader>

            <div className="mb-4.5 lg:mb-7">
              <AtText
                className="mb-2 text-skin-tertiary lg:mb-3"
                variant={ETextVariants.LABEL}
              >
                Exterior Style
              </AtText>
              <AtHeader
                element="p"
                variant="h6"
                className="text-skin-secondary"
              >
                {selectedExterior?.name}
              </AtHeader>
            </div>

            <div className="mb-5">
              <span className="mb-2 flex items-center lg:mb-3.5">
                <AtText
                  className="mr-0.6 text-skin-tertiary"
                  variant={ETextVariants.LABEL}
                >
                  Exterior Price
                </AtText>
                {heroTooltipBody && (
                  <MlInfoTooltip
                    iconName={EIcons.INFO}
                    body={heroTooltipBody}
                  />
                )}
              </span>

              {typeof price === 'number' ? (
                <AtHeader className="md:mb-12" element="p" variant="h3" alt>
                  ${price.toLocaleString()} +
                </AtHeader>
              ) : locationConflict ? (
                <AtHeader className="md:mb-12" element="p" variant="h3" alt>
                  Not Available
                </AtHeader>
              ) : (
                <AtClickable
                  label="Set Location"
                  variant={EClickableVariants.PRIMARY}
                  outline
                  iconPosition="left"
                  onClick={() => handleOpen({ type: ESetLocationType.DEFAULT })}
                  interactLocation="tm-home-plan-detail"
                  shouldSendGTMEvent={false}
                >
                  <AtIcon
                    icon={EIcons.LOCATION}
                    color="var(--color-ui-icon-primary)"
                  />
                </AtClickable>
              )}
            </div>

            {!locationConflict && !is3DLoading && (
              <AtClickable
                label={has3D ? 'Customize in 3D' : 'Customize & Price'}
                variant={EClickableVariants.PRIMARY}
                iconPosition="left"
                interactLocation="tm-home-plan-detail"
                onClick={handleCustomizeAndPrice}
                href={currentBuildStudio?.Id ? customizeAndPriceUrl : undefined}
              >
                <AtIcon
                  icon={EIcons.THREED}
                  color="var(--color-button-text-primary)"
                />
              </AtClickable>
            )}
          </div>
        }
      />
    </>
  );
};
